import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
  inject
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ColumnRow, EkoGenericTableComponent, TableConfig } from '@ekowitryna/ui';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import type {
  MixtureCalculationDto,
  MixtureRecordCalculationDto,
  SubstanceAutocompleteDto
} from '@mobiletract/kobize-service-angular-client';
import {
  MixtureRecordCalculationDto as MixtureRecordCalculationDtoModel,
  ReportsControllerService,
  SubstancesControllerService
} from '@mobiletract/kobize-service-angular-client';
import { MessageService } from 'primeng/api';
import { AutoCompleteCompleteEvent, AutoCompleteModule } from 'primeng/autocomplete';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { MessageModule } from 'primeng/message';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { firstValueFrom } from 'rxjs';
import {
  formatPercentageInput,
  normalizePercentageForApi,
  percentageInputValidator
} from './percentage-input.util';

const AUTOCOMPLETE_NAME_MAX_LENGTH = 72;

interface SubstanceAutocompleteOption extends SubstanceAutocompleteDto {
  casDisplay: string;
  nameDisplay: string | null;
  nameFull: string | null;
  inputLabel: string;
}

@Component({
  selector: 'app-report-mixture-calculation-card',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    ReactiveFormsModule,
    EkoGenericTableComponent,
    ButtonModule,
    AutoCompleteModule,
    InputTextModule,
    DialogModule,
    TagModule,
    MessageModule,
    TooltipModule
  ],
  templateUrl: './report-mixture-calculation-card.component.html',
  styleUrl: './report-mixture-calculation-card.component.css'
})
export class ReportMixtureCalculationCardComponent implements OnChanges {
  private readonly messageService = inject(MessageService);
  private readonly translate = inject(TranslateService);
  private readonly reportsApi = inject(ReportsControllerService);
  private readonly substancesApi = inject(SubstancesControllerService);
  private readonly destroyRef = inject(DestroyRef);

  @ViewChild('recordsTable') recordsTable?: EkoGenericTableComponent;

  @Input({ required: true }) mixture!: MixtureCalculationDto;
  @Input({ required: true }) reportId!: string | null;
  @Input({ required: true }) companyId!: string | null;

  @Output() editMixture = new EventEmitter<MixtureCalculationDto>();
  @Output() mixtureDeleted = new EventEmitter<void>();
  @Output() mixtureChanged = new EventEmitter<void>();

  deleteDialogVisible = false;
  deleteSaving = false;
  recordDialogVisible = false;
  recordDialogSaving = false;
  recordDeleteDialogVisible = false;
  recordDeleteSaving = false;
  recordToDelete: MixtureRecordCalculationDto | null = null;
  recordAutocompleteLoading = false;
  recordAutocompleteItems: SubstanceAutocompleteOption[] = [];
  recordEditId: number | null = null;
  recordEditCasNumber: string | null = null;
  recordEditDisplayLabel = '';

  readonly recordForm = new FormGroup({
    substance: new FormControl<SubstanceAutocompleteOption | string | null>(null, {
      validators: [Validators.required]
    }),
    usagePercentage: new FormControl<string | null>(null, {
      validators: [Validators.required, percentageInputValidator()]
    })
  });

  recordColumnRows: ColumnRow[] = [];
  recordTableConfig: TableConfig = {
    paginator: false,
    loading: false,
    totalRecords: 0,
    filterable: false,
    editable: false,
    actionsPosition: 'right',
    first: 0,
    dataKey: 'id',
    reorderableColumns: false,
    resizableColumns: true,
    columnResizeMode: 'fit',
    scrollable: false,
    stateStorage: 'local',
    stateKey: 'kobize_table-mixture-records-unknown',
    selectionMode: null,
    tableMenuItems: []
  };

  readonly recordDataProvider = async (): Promise<{
    data: MixtureRecordCalculationDto[];
    totalRecords: number;
  }> => ({
    data: this.recordRows,
    totalRecords: this.recordRows.length
  });

  constructor() {
    this.setupRecordColumns();
    this.translate.onLangChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.setupRecordColumns();
      this.recordsTable?.reload();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['reportId'] || changes['mixture']) {
      this.recordTableConfig.totalRecords = this.recordRows.length;
      this.recordTableConfig.stateKey = this.buildRecordTableStateKey();
      if (!changes['mixture']?.firstChange && !changes['reportId']?.firstChange) {
        this.recordsTable?.reload();
      }
    }
  }

  openRecordDialogCreate(): void {
    this.recordDialogVisible = true;
    this.recordEditId = null;
    this.recordEditCasNumber = null;
    this.recordEditDisplayLabel = '';
    this.recordAutocompleteItems = [];
    this.recordDialogSaving = false;
    this.recordForm.reset({
      substance: null,
      usagePercentage: null
    });
    this.recordForm.get('substance')?.enable({ emitEvent: false });
  }

  openRecordDialogEdit(row: MixtureRecordCalculationDto): void {
    this.recordDialogVisible = true;
    this.recordEditId = row.id;
    this.recordEditCasNumber = row.casNumber;
    this.recordEditDisplayLabel = this.formatAutocompleteInputLabel(
      this.displayCasNumber(row.casNumber),
      row.kbName ?? row.chargingSystemName
    );
    this.recordAutocompleteItems = [];
    this.recordDialogSaving = false;
    this.recordForm.reset({
      substance: null,
      usagePercentage: formatPercentageInput(row.usagePercentage)
    });
    this.recordForm.get('substance')?.disable({ emitEvent: false });
  }

  closeRecordDialog(): void {
    if (this.recordDialogSaving) {
      return;
    }
    this.recordDialogVisible = false;
  }

  openRecordDeleteDialog(row: MixtureRecordCalculationDto): void {
    if (this.mixture?.type === 'WELDING') {
      return;
    }
    this.recordToDelete = row;
    this.recordDeleteSaving = false;
    this.recordDeleteDialogVisible = true;
  }

  cancelRecordDeleteDialog(): void {
    if (this.recordDeleteSaving) {
      return;
    }
    this.recordDeleteDialogVisible = false;
    this.recordToDelete = null;
  }

  async confirmRecordDelete(): Promise<void> {
    const rid = this.reportId;
    const cid = this.companyId;
    const row = this.recordToDelete;
    if (!rid || !cid || !row) {
      this.recordDeleteDialogVisible = false;
      this.recordToDelete = null;
      return;
    }

    this.recordDeleteSaving = true;
    try {
      await firstValueFrom(this.reportsApi.deleteMixtureRecord(rid, this.mixture.id, row.id, cid));
      this.recordDeleteDialogVisible = false;
      this.recordToDelete = null;
      this.mixtureChanged.emit();
      this.messageService.add({
        severity: 'success',
        summary: this.translate.instant('reports.toast.successTitle'),
        detail: this.translate.instant('reports.fill.recordDialog.deleteSuccess')
      });
    } catch (err: unknown) {
      const detail =
        this.mapHttpErrorDetail(err) ?? this.translate.instant('reports.fill.recordDialog.deleteFailed');
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('reports.toast.errorTitle'),
        detail
      });
    } finally {
      this.recordDeleteSaving = false;
    }
  }

  async searchSubstances(event: AutoCompleteCompleteEvent): Promise<void> {
    const term = String(event.query ?? '').trim();
    const companyId = this.companyId;
    if (!companyId || term.length === 0 || this.isRecordEditMode) {
      this.recordAutocompleteItems = [];
      return;
    }

    this.recordAutocompleteLoading = true;
    try {
      const suggestions = await firstValueFrom(
        this.substancesApi.autocompleteSubstances(term, companyId, 20)
      );
      this.recordAutocompleteItems = suggestions.map((item) => this.toAutocompleteOption(item));
    } catch {
      this.recordAutocompleteItems = [];
    } finally {
      this.recordAutocompleteLoading = false;
    }
  }

  async saveRecord(): Promise<void> {
    this.recordForm.markAllAsTouched();
    if (!this.reportId || !this.companyId || !this.recordForm.valid) {
      return;
    }

    const usagePercentageInput = this.recordForm.get('usagePercentage')!.value;
    const usagePercentage = normalizePercentageForApi(usagePercentageInput);
    if (!usagePercentage) {
      return;
    }
    const casNumber = this.isRecordEditMode
      ? this.recordEditCasNumber
      : this.resolveCasFromSelection(this.recordForm.get('substance')!.value);

    if (!casNumber) {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('reports.toast.errorTitle'),
        detail: this.translate.instant('reports.fill.recordDialog.casInvalid')
      });
      return;
    }

    this.recordDialogSaving = true;
    try {
      if (this.isRecordEditMode) {
        await firstValueFrom(
          this.reportsApi.updateMixtureRecord(
            this.reportId,
            this.mixture.id,
            this.recordEditId as number,
            this.companyId,
            { casNumber, usagePercentage: usagePercentage as unknown as number }
          )
        );
      } else {
        await firstValueFrom(
          this.reportsApi.addMixtureRecord(this.reportId, this.mixture.id, this.companyId, {
            casNumber,
            usagePercentage: usagePercentage as unknown as number
          })
        );
      }

      this.recordDialogVisible = false;
      this.mixtureChanged.emit();
      this.messageService.add({
        severity: 'success',
        summary: this.translate.instant('reports.toast.successTitle'),
        detail: this.translate.instant('reports.fill.recordDialog.saveSuccess')
      });
    } catch (err: unknown) {
      const detail =
        this.mapHttpErrorDetail(err) ?? this.translate.instant('reports.fill.recordDialog.saveFailed');
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('reports.toast.errorTitle'),
        detail
      });
    } finally {
      this.recordDialogSaving = false;
    }
  }

  onEditClick(): void {
    this.editMixture.emit(this.mixture);
  }

  openDeleteDialog(): void {
    this.deleteDialogVisible = true;
  }

  cancelDeleteMixture(): void {
    if (!this.deleteSaving) {
      this.deleteDialogVisible = false;
    }
  }

  async confirmDeleteMixture(): Promise<void> {
    const rid = this.reportId;
    const cid = this.companyId;
    if (!rid || !cid) {
      this.deleteDialogVisible = false;
      return;
    }

    this.deleteSaving = true;
    try {
      await firstValueFrom(this.reportsApi.deleteMixture(rid, this.mixture.id, cid));
      this.deleteDialogVisible = false;
      this.mixtureDeleted.emit();
    } catch (err: unknown) {
      const detail =
        this.mapHttpErrorDetail(err) ?? this.translate.instant('reports.fill.mixtureDeleteFailed');
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('reports.toast.errorTitle'),
        detail
      });
    } finally {
      this.deleteSaving = false;
    }
  }

  private mapHttpErrorDetail(err: unknown): string {
    const fallback = this.translate.instant('common.somethingWentWrong');
    if (!(err instanceof HttpErrorResponse)) {
      return fallback;
    }
    const body = err.error;
    if (body && typeof body === 'object') {
      const msg = (body as Record<string, unknown>)['message'];
      if (typeof msg === 'string' && msg.trim().length > 0) {
        return msg.trim();
      }
      return fallback;
    }
    if (typeof body === 'string') {
      const text = body.trim();
      if (text.length === 0) {
        return fallback;
      }
      try {
        const parsed = JSON.parse(text) as Record<string, unknown>;
        const msg = parsed['message'];
        if (typeof msg === 'string' && msg.trim().length > 0) {
          return msg.trim();
        }
      } catch {
        return fallback;
      }
    }
    return fallback;
  }

  get recordRows(): NonNullable<MixtureCalculationDto['recordsCalculations']> {
    return this.mixture?.recordsCalculations ?? [];
  }

  private buildRecordTableStateKey(): string {
    const reportPart = String(this.reportId ?? 'unknown-report').trim() || 'unknown-report';
    const mixturePart = String(this.mixture?.id ?? 'unknown-mixture').trim() || 'unknown-mixture';
    return `kobize_table-mixture-records-${reportPart}-${mixturePart}`;
  }

  private setupRecordColumns(): void {
    this.recordColumnRows = [
      {
        columns: [
          {
            field: 'casNumber',
            type: 'text',
            header: this.translate.instant('reports.fill.columns.cas'),
            load: (row: MixtureRecordCalculationDto) => this.displayCasNumber(row.casNumber),
            resizable: true
          },
          {
            field: 'usagePercentage',
            type: 'text',
            header: this.translate.instant('reports.fill.columns.sharePercent'),
            load: (row: MixtureRecordCalculationDto) =>
              `${row.usagePercentage?.toLocaleString(undefined, { maximumFractionDigits: 8 }) ?? '0'} %`,
            resizable: true
          },
          {
            field: 'kbName',
            type: 'text',
            header: this.translate.instant('reports.fill.columns.substanceName'),
            load: (row: MixtureRecordCalculationDto) => row.kbName ?? '—',
            resizable: true
          },
          {
            field: 'chargingSystemName',
            type: 'text',
            header: this.translate.instant('reports.fill.columns.chargingSystem'),
            load: (row: MixtureRecordCalculationDto) => row.chargingSystemName ?? '—',
            resizable: true
          },
          {
            field: 'emissionFactor',
            type: 'text',
            header: this.translate.instant('reports.fill.columns.emissionFactor'),
            load: (row: MixtureRecordCalculationDto) =>
              this.isRecordCalculable(row)
                ? `${row.emissionFactor?.toLocaleString(undefined, { maximumFractionDigits: 8 }) ?? '0'} ${this.translate.instant('reports.fill.unitKgPerMg')}`
                : this.translate.instant('reports.fill.recordMissingRateShort'),
            resizable: true
          },
          {
            field: 'recordEmission',
            type: 'text',
            header: this.translate.instant('reports.fill.columns.emission'),
            load: (row: MixtureRecordCalculationDto) =>
              this.isRecordCalculable(row)
                ? `${row.recordEmission?.toLocaleString(undefined, { maximumFractionDigits: 8 }) ?? '0'} ${this.translate.instant('reports.fill.unitKg')}`
                : this.translate.instant('reports.fill.recordMissingRateShort'),
            resizable: true
          }
        ]
      }
    ];
  }

  isMissingAnnualRate(row: MixtureRecordCalculationDto): boolean {
    return row.status === MixtureRecordCalculationDtoModel.StatusEnum.MissingAnnualRate;
  }

  isMissingSubstance(row: MixtureRecordCalculationDto): boolean {
    return row.status === MixtureRecordCalculationDtoModel.StatusEnum.MissingSubstance;
  }

  isRecordCalculable(row: MixtureRecordCalculationDto): boolean {
    if (typeof row.isCalculable === 'boolean') {
      return row.isCalculable;
    }
    return row.status === MixtureRecordCalculationDtoModel.StatusEnum.Ok;
  }

  getRecordValidationMessage(row: MixtureRecordCalculationDto): string | null {
    if (row.validationMessage && row.validationMessage.trim().length > 0) {
      return row.validationMessage;
    }
    if (this.isMissingAnnualRate(row)) {
      return this.translate.instant('reports.fill.recordMissingAnnualRate');
    }
    if (this.isMissingSubstance(row)) {
      return this.translate.instant('reports.fill.recordMissingSubstance');
    }
    return null;
  }

  displayCasNumber(casNumber: string | null | undefined): string {
    return this.extractCas(casNumber) ?? '';
  }


  /** Same rule as backend: total emission factor sum must not exceed 1000 g/kg. */
  get isTotalEmissionFactorOverLimit(): boolean {
    return (this.mixture?.totalEmissionFactor ?? 0) > 1000;
  }

  /** Banner excludes the "over 1000 g/kg" notice — that is shown on the factor sum + tooltip instead. */
  get mixtureBannerErrorsText(): string {
    return this.mixtureBannerErrors.map((e) => e.message).join('; ');
  }

  get mixtureBannerErrors(): Array<{ message: string }> {
    return (this.mixture?.errors ?? []).filter((e) => !this.isEmissionFactorLimitBackendMessage(e.message));
  }

  private isEmissionFactorLimitBackendMessage(message: string): boolean {
    const m = message.trim().toLowerCase();
    return m.includes('1000') && (m.includes('g/kg') || m.includes('przekracza'));
  }

  get mixtureBadgeLabelKey(): string {
    return this.mixture?.type === 'WELDING'
      ? 'reports.fill.mixtureDialog.typeWelding'
      : 'reports.fill.mixtureBadge';
  }

  get isRecordEditMode(): boolean {
    return this.recordEditId != null;
  }

  get recordDialogHeaderKey(): string {
    return this.isRecordEditMode
      ? 'reports.fill.recordDialog.titleEdit'
      : 'reports.fill.recordDialog.titleCreate';
  }

  recordFieldError(controlName: 'substance' | 'usagePercentage'): string | null {
    const control = this.recordForm.get(controlName);
    if (!control || !control.touched || !control.errors) {
      return null;
    }
    if (control.errors['required']) {
      return this.translate.instant('reports.form.fieldRequired');
    }
    if (control.errors['min']) {
      return controlName === 'usagePercentage'
        ? this.translate.instant('reports.fill.mixtureDialog.errorMinPercent')
        : this.translate.instant('reports.fill.mixtureDialog.errorMin');
    }
    if (control.errors['max']) {
      return this.translate.instant('reports.fill.mixtureDialog.errorMaxPercent');
    }
    if (control.errors['invalidDecimal'] || control.errors['maxFractionDigits']) {
      return this.translate.instant('reports.fill.mixtureDialog.errorInvalidPercentFormat');
    }
    return null;
  }

  private resolveCasFromSelection(
    selection: SubstanceAutocompleteOption | string | null
  ): string | null {
    if (!selection) {
      return null;
    }
    if (typeof selection === 'string') {
      return this.extractCas(selection) ?? (selection.trim() || null);
    }
    return (
      this.extractCas(selection.value) ??
      this.extractCas(selection.id) ??
      this.extractCas(selection.label) ??
      selection.value ??
      selection.id ??
      null
    );
  }

  private toAutocompleteOption(item: SubstanceAutocompleteDto): SubstanceAutocompleteOption {
    const casDisplay = this.extractAutocompleteCas(item);
    const nameFull = this.extractAutocompleteName(item, casDisplay);
    const nameDisplay = this.limitAutocompleteName(nameFull);

    return {
      ...item,
      label: this.formatAutocompleteInputLabel(casDisplay, nameDisplay),
      inputLabel: this.formatAutocompleteInputLabel(casDisplay, nameDisplay),
      casDisplay,
      nameDisplay,
      nameFull: nameFull || null
    };
  }

  private normalizeAutocompleteLabel(item: SubstanceAutocompleteDto): string {
    const cas =
      this.extractAutocompleteCas(item);
    const sourceLabelWithoutCas = this.extractAutocompleteName(item, cas);
    return this.formatAutocompleteInputLabel(cas, sourceLabelWithoutCas);
  }

  private extractAutocompleteCas(item: SubstanceAutocompleteDto): string {
    return (
      this.extractCas(item.value) ??
      this.extractCas(item.id) ??
      this.extractCas(item.label) ??
      String(item.value ?? '').trim()
    );
  }

  private extractAutocompleteName(item: SubstanceAutocompleteDto, cas: string): string {
    const sourceLabel = (item.label ?? '').trim();
    if (!cas) {
      return sourceLabel;
    }
    return sourceLabel.replace(cas, '').trim().replace(/^[()\s-]+|[()\s-]+$/g, '');
  }

  private formatAutocompleteInputLabel(casNumber: string, name?: string | null): string {
    return this.formatCasLabel(casNumber, this.limitAutocompleteName(name ?? ''));
  }

  private limitAutocompleteName(name: string): string | null {
    const normalizedName = name.trim();
    if (normalizedName.length === 0) {
      return null;
    }
    if (normalizedName.length <= AUTOCOMPLETE_NAME_MAX_LENGTH) {
      return normalizedName;
    }
    return `${normalizedName.slice(0, AUTOCOMPLETE_NAME_MAX_LENGTH).trimEnd()}...`;
  }

  private formatCasLabel(casNumber: string, name?: string | null): string {
    const normalizedName = (name ?? '').trim();
    if (casNumber.length === 0) {
      return normalizedName;
    }
    return normalizedName.length > 0 ? `${casNumber} (${normalizedName})` : casNumber;
  }

  private extractCas(text: string | null | undefined): string | null {
    if (!text) {
      return null;
    }
    const match = text.match(/\d{2,7}-\d{2}-\d/);
    return match ? match[0] : null;
  }
}
